import React, {useCallback, useMemo, useState} from 'react'
import {useParams} from 'react-router'
import type {IPerson, ITask} from '../../../../types'
import {checkValidation} from '../../../../utils'
import {empowerApi} from '../../../../api/empowerApi'
import {TASK_STATUS} from '../../constants/taskConstants'
import {taskRowUtil} from '../../utils/taskRowUtil'
import {tasksApi} from '../../api/tasksApi'
import {BaseModal} from '../../../../components/BaseModal/BaseModal'
import {ErrorLabel} from '../../../../components/ErrorLabel/ErrorLabel'
import {ReactComponent as Asterisk} from '../../../../assets/Icons/Asterisk.svg'
import {ReactComponent as Question} from '../../../../assets/Icons/Question.svg'
import {TASK_ACTIONS_HELPFILE} from '../../../../constants/helpFileLinksConstants'
import {
  useWt,
  WuButton,
  WuCombobox,
  WuDatePicker,
  WuInput,
  WuSelect,
  WuTextarea,
} from '@npm-questionpro/wick-ui-lib'
import {GLOBAL} from '../../../../Translation/English/global'
import {TASK} from '../../../../Translation/English/task'
import styles from './AddOrEditTaskModal.module.scss'

interface IAddOrEditTaskModalProps {
  isShowModal: boolean
  closeAddTaskModal: (result: {isCreated: boolean}) => void
  selectTask?: ITask
  /** Used by Ideation's actionable insights: seed the form from `selectTask`
   *  but always create, never update. */
  onlyAdd?: boolean
  initID?: string
}

/** A person as the pickers see them. `emailAddress` is the identity key — tasks
 *  store owners as denormalised JSON with no foreign key to a user row. */
interface IPersonOption {
  value: string
  label: string
  person?: IPerson
}

/** Sentinel for the "no owner" entry. Cannot collide with a real address. */
const NO_OWNER_VALUE = '__no_owner__'

const toOption = (person: IPerson): IPersonOption => ({
  value: person.emailAddress ?? String(person.id ?? person.username ?? ''),
  label: person.username || person.emailAddress || '',
  person,
})

/** Contributors and owners are rendered by `People`, which needs a colour. The
 *  server assigns one too, but not before the optimistic render. */
const withColor = (person: IPerson): IPerson =>
  person.color
    ? person
    : {
        ...person,
        color: `rgb(${Math.round(Math.random() * 255)}, ${Math.round(
          Math.random() * 255,
        )}, ${Math.round(Math.random() * 240)})`,
      }

export const AddOrEditTaskModal: React.FC<IAddOrEditTaskModalProps> = ({
  isShowModal,
  closeAddTaskModal,
  selectTask,
  onlyAdd = false,
  initID: propInitID,
}) => {
  const wt = useWt()
  const {initID: paramInitID} = useParams<{initID: string}>()
  const initID = propInitID ?? paramInitID

  const isEditing = !!selectTask && !onlyAdd

  const {mutateAsync: createTask, isPending: isCreating} =
    tasksApi.useCreateTask()
  const {mutateAsync: updateTask, isPending: isUpdating} =
    tasksApi.useUpdateTask()
  const isPending = isCreating || isUpdating

  const {data: peopleOptions = []} = empowerApi.useInitiativePeoples(initID)

  const [task, setTask] = useState<ITask>(() => ({
    name: selectTask?.name || '',
    description: selectTask?.description || '',
    dueDate: selectTask?.dueDate || taskRowUtil.defaultDueDate(),
    // `responsible` comes back as an object from GET /tasks but the column
    // stores an array — read through the util so both shapes work.
    responsible: taskRowUtil.readOwner(selectTask?.responsible ?? null) ?? {},
    people: selectTask?.people || [],
  }))
  const [hasNameError, setHasNameError] = useState(false)

  const ownerOptions = useMemo<IPersonOption[]>(
    () => [
      ...(peopleOptions as IPerson[]).map(toOption),
      {value: NO_OWNER_VALUE, label: String(wt(TASK.noOwner))},
    ],
    [peopleOptions, wt],
  )

  const contributorOptions = useMemo<IPersonOption[]>(
    () => (peopleOptions as IPerson[]).map(toOption),
    [peopleOptions],
  )

  const selectedOwner = useMemo<IPersonOption | null>(() => {
    const owner = task.responsible as IPerson
    if (!owner?.emailAddress) return null
    return (
      ownerOptions.find(option => option.value === owner.emailAddress) ??
      toOption(owner)
    )
  }, [ownerOptions, task.responsible])

  const selectedContributors = useMemo<IPersonOption[]>(
    () => (task.people ?? []).map(toOption),
    [task.people],
  )

  const handleSelectOwner = useCallback(
    (selection: IPersonOption | IPersonOption[]): void => {
      const option = Array.isArray(selection) ? selection[0] : selection
      setTask(prev => ({
        ...prev,
        responsible:
          !option || option.value === NO_OWNER_VALUE || !option.person
            ? {}
            : withColor(option.person),
      }))
    },
    [],
  )

  const handleSelectContributors = useCallback(
    (selection: IPersonOption | IPersonOption[]): void => {
      const options = Array.isArray(selection) ? selection : [selection]
      setTask(prev => ({
        ...prev,
        people: options
          .filter((option): option is IPersonOption => !!option?.person)
          .map(option => withColor(option.person as IPerson)),
      }))
    },
    [],
  )

  const handleSubmit = useCallback(async (): Promise<void> => {
    const {error} = checkValidation({name: task.name})
    if (error) {
      setHasNameError(true)
      return
    }

    try {
      if (isEditing) {
        await updateTask({id: String(selectTask?.id), task})
        closeAddTaskModal({isCreated: false})
        return
      }
      await createTask({
        ...task,
        // Ideation seeds this modal with a task that already knows its
        // initiative; the route param is not that initiative.
        initiativeId: onlyAdd && selectTask ? selectTask.initiativeId : initID,
        status: TASK_STATUS.pending,
      })
      closeAddTaskModal({isCreated: true})
    } catch {
      // Surfaced as a toast by the mutation's onError; the modal stays open.
    }
  }, [
    closeAddTaskModal,
    createTask,
    initID,
    isEditing,
    onlyAdd,
    selectTask,
    task,
    updateTask,
  ])

  return (
    <BaseModal
      visible={isShowModal}
      isLoading={isPending}
      onCloseModal={() => closeAddTaskModal({isCreated: false})}
      headerTitle={
        <div className={styles.headerTitle}>
          {isEditing ? `${wt(TASK.updateTask)}` : `${wt(TASK.newTask)}`}
          <a
            href={TASK_ACTIONS_HELPFILE}
            target="_blank"
            rel="noreferrer"
            aria-label={wt(GLOBAL.help)}
            className={styles.helpLink}
          >
            <Question />
          </a>
        </div>
      }
      footer={
        <div className={styles.footer}>
          <WuButton
            variant="secondary"
            disabled={isPending}
            onClick={() => closeAddTaskModal({isCreated: false})}
          >
            {wt(GLOBAL.cancel)}
          </WuButton>
          <WuButton
            variant="primary"
            loading={isPending}
            disabled={isPending}
            onClick={() => void handleSubmit()}
          >
            {isEditing ? `${wt(GLOBAL.saveChanges)}` : `${wt(TASK.createTask)}`}
          </WuButton>
        </div>
      }
    >
      <div className={styles.addTaskModal} data-testid="add-task-modal">
        <div className={styles.contentBlock}>
          <div
            className={styles.contentBlockName}
            data-testid="add-task-modal-name-block"
          >
            <p className={styles.title}>
              {wt(GLOBAL.name)} <Asterisk className={styles.asteriskRequired} />
            </p>
            <WuInput
              variant="outlined"
              placeholder={String(wt(GLOBAL.typeHere))}
              value={task.name}
              invalid={hasNameError}
              aria-label={wt(GLOBAL.name)}
              onChange={event => {
                setTask(prev => ({...prev, name: event.target.value}))
                setHasNameError(false)
              }}
            />
            {hasNameError ? <ErrorLabel title={`${wt(GLOBAL.name)}`} /> : null}
          </div>

          <div className={styles.contentBlockDesc}>
            <p className={styles.title}>{wt(GLOBAL.description)}</p>
            <WuTextarea
              id="task-description-input"
              className={styles.description}
              placeholder={String(wt(GLOBAL.typeHere))}
              value={task.description ?? ''}
              aria-label={wt(GLOBAL.description)}
              onChange={event =>
                setTask(prev => ({...prev, description: event.target.value}))
              }
            />
          </div>

          <div
            className={styles.contentBlockDiv}
            data-testid="add-task-modal-date-dropdown"
          >
            <div className={styles.contentBlockDivChild}>
              <p className={styles.title}>{wt(GLOBAL.dueDate)}</p>
              <WuDatePicker
                formatString="MMMM d, yyyy"
                value={task.dueDate}
                onChange={date =>
                  date && setTask(prev => ({...prev, dueDate: date}))
                }
                variant="outlined"
                className="wui-date-picker"
              />
            </div>
            <div className={styles.contentBlockDivChild}>
              <p className={styles.title}>{wt(GLOBAL.owner)}</p>
              <WuSelect<IPersonOption>
                id="responsible-dropdown"
                data={ownerOptions}
                accessorKey={{value: 'value', label: 'label'}}
                value={selectedOwner}
                onSelect={handleSelectOwner}
                variant="outlined"
                placeholder={String(wt(TASK.selectOwner))}
                className={styles.dropdownFullWidth}
              />
            </div>
          </div>

          <div className={styles.contentBlockContributors}>
            <p className={styles.title}>{wt(GLOBAL.contributors)}</p>
            <WuCombobox<IPersonOption>
              id="collaborator--dropdown"
              data={contributorOptions}
              accessorKey={{value: 'value', label: 'label'}}
              value={selectedContributors}
              onSelect={handleSelectContributors}
              multiple
              enableSearch
              variant="outlined"
              placeholder={String(wt(TASK.selectContributor))}
              className={styles.dropdownFullWidth}
            />
          </div>
        </div>
      </div>
    </BaseModal>
  )
}
